/* eslint-disable @typescript-eslint/no-unused-vars */
import { useSelector } from "react-redux";
import { ChestImage, Container, StarBurst } from "./styles";
import { bouncyChestAssets } from "assets/cdn";
import { RootState } from "redux/store";
import { useLocation } from "react-router-dom";
import starBurst from "assets/start-burst-reward.png";
import {
	getDIPs,
	onDIPtrigger,
	selectDipState,
	selectWebStoreState
} from "redux/slices";
import { useAppDispatch, useAppSelector, useImagePath } from "hooks";
import { BouncyChestResponse } from "interfaces/bouncy-chest";
import { useGetChestStateQuery } from "redux/apis/bouncyChest";
import useNewDay from "hooks/useNewDay";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	LOCAL_STORAGE_LOGIN_FROM,
	useGoogleAnalyticsContext,
	useWebStoreContext
} from "contexts";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { WsDialogs as IDialogVariants } from "interfaces/dialog/variant";
import { offersApi } from "redux/apis";
import { IShopPurchaseOffer } from "contexts/WebStoreProvider/types";
import { getVariantImage } from "helpers/getVariantImage";
import { useMediaQuery } from "@mui/material";
import { useMediaPreloader } from "hooks/useMediaPreloader";
import { useLinks } from "hooks/useLinks";
import { DIPS_IMAGE_VARIANT_TAG } from "components/DIP/helper";

interface Props {
	from?: string;
}

export const BouncyChest: React.FC<Props> = ({ from }) => {
	const { getImage } = useImagePath("cdn");
	const chestImage = getImage(bouncyChestAssets.main.chest);
	const location = useLocation();
	const isMegaMenuOpen = useSelector(
		(state: RootState) => state.megaMenu.isOpen
	);
	const TIMEOUT_OVERFLOW = 2000000000;
	const isHomePage = location.pathname === "/";
	const dispatch = useAppDispatch();
	const { newDay } = useNewDay();
	const { authToken } = useWebStoreContext();
	const { openDialog } = useDialogModalRedux();
	const { data: dipData } = useAppSelector(selectDipState);
	const { nowTime } = useWebStoreContext();
	const { bouncyChestConfigs } = useAppSelector(selectWebStoreState).store;

	const { data: purchaseOffers } = offersApi.endpoints.getOffers.useQueryState({
		offerType: "singleItem",
		isAuthenticated: true
	});
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();

	const [isPollingActive, setIsPollingActive] = useState(false);
	const pollingTime = 60 * 60 * 1000;
	const isPortrait = useMediaQuery("(orientation: portrait)");
	const links = useLinks();

	const {
		data,
		isLoading,
		refetch
	}: {
		data?: BouncyChestResponse;
		isLoading: boolean;
		refetch: () => void;
	} = useGetChestStateQuery("", {
		pollingInterval: isPollingActive ? pollingTime : 0
	});

	const bouncyDip = useMemo(
		() => dipData?.dips?.find((d) => d.dip.triggerType === "bouncyChestClick"),
		[dipData]
	);

	//* Start - Preload media

	const url = bouncyDip?.dip?.imageUrl as string;
	const variantUrl = useMemo(() => {
		return getVariantImage(
			url,
			DIPS_IMAGE_VARIANT_TAG,
			isPortrait ? "small" : "large"
		);
	}, [url, isPortrait]);
	const { isMediaLoaded } = useMediaPreloader(variantUrl);

	//* End - Preload media

	const bouncyChestConfig = useMemo(
		() =>
			bouncyChestConfigs?.find(
				(config) => config.id === bouncyDip?.dip?.bouncyChestConfigId
			),
		[bouncyDip, bouncyChestConfigs]
	);

	const onClick = () => {
		if (!authToken?.value) {
			openDialog(IDialogVariants.LOGIN_TO_CONNECT, { source: "bouncyChest" });
			return;
		}

		if (!isDisabled()) {
			dispatch(onDIPtrigger({ triggerType: "bouncyChestClick" }));

			//* Start Google Analytics

			const offerId = bouncyDip?.buttons && bouncyDip.buttons[0].offerId;

			const offer = purchaseOffers?.offers.find(
				(offer: IShopPurchaseOffer) => offer.id === offerId
			) as IShopPurchaseOffer;

			reportEvent("bouncy_chest", {
				source: "click",
				id: offer?.id,
				semented_id: offer?.segmentId,
				page_location: location.pathname,
				via_mega_menu: from === "mega-menu"
			});
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "bouncy_chest",
				event_type: "bouncy_chest",
				action: "click",
				source: from,
				page_location: location.pathname,
				offer_id: offer?.id,
				event_id: bouncyDip?.id,
				segment_id: offer?.segmentId,
				theme_id: bouncyDip?.dip?.bouncyChestConfigId,
				tag_type: from === "mega-menu"
			});
			//* End Google Analytics
		}
	};

	const isDisabled = useCallback(() => {
		return (
			!isMediaLoaded ||
			isLoading ||
			!data?.isActiveSale ||
			(!authToken?.value &&
				from === undefined &&
				location.pathname === links.shop.path) ||
			(authToken?.value &&
				(!purchaseOffers ||
					!(
						bouncyDip &&
						(!bouncyDip.dip.endTime ||
							new Date(bouncyDip.dip.endTime) >
								new Date(nowTime?.format() || ""))
					)))
		);
	}, [
		isLoading,
		data,
		authToken,
		from,
		purchaseOffers,
		bouncyDip,
		nowTime,
		location,
		isMediaLoaded
	]);

	const isClaimed = useCallback(() => {
		return (
			authToken?.value && data?.isActiveSale && dipData?.dips && !bouncyDip
		);
	}, [authToken, data, purchaseOffers, dipData, bouncyDip, nowTime]);

	/**
	 * When there is no active sale but the API provides the next start time,
	 * refresh the chest state and DIPs exactly when that sale should start.
	 */
	useEffect(() => {
		if (!data?.isActiveSale && data?.nextActiveSaleDate) {
			// Prevent timeout overflow
			const timeUntilActiveSale = Math.min(
				new Date(data?.nextActiveSaleDate || "").getTime() -
					new Date(nowTime?.format() || "").getTime(),
				TIMEOUT_OVERFLOW
			);
			// Prevent negative numbers
			if (timeUntilActiveSale < 0) {
				return;
			}

			const timeoutId = setTimeout(() => {
				refetch();
				dispatch(getDIPs());
			}, timeUntilActiveSale);

			return () => clearTimeout(timeoutId);
		}
	}, [data, nowTime]);

	/**
	 * When the current Bouncy Chest DIP expires, refresh both the chest state
	 * and the DIPs. Only the floating instance owns this timer to avoid duplicate
	 * requests from the Mega Menu instances.
	 */
	useEffect(() => {
		if (from !== undefined) {
			return;
		}

		const endTime = bouncyDip?.dip?.endTime;

		if (!endTime || !nowTime) {
			return;
		}

		const timeUntilEnd = Math.min(
			new Date(endTime).getTime() - new Date(nowTime.toISOString()).getTime(),
			TIMEOUT_OVERFLOW
		);

		if (Number.isNaN(timeUntilEnd)) {
			return;
		}

		const timeoutId = setTimeout(() => {
			refetch();
			dispatch(getDIPs());
		}, Math.max(0, timeUntilEnd));

		return () => clearTimeout(timeoutId);
	}, [from, bouncyDip?.dip?.endTime, nowTime, refetch, dispatch]);

	/**
	 * Enable the one-hour RTK Query fallback polling only when the API does not
	 * provide a next sale date.
	 */
	useEffect(() => {
		setIsPollingActive(!data?.isActiveSale && !data?.nextActiveSaleDate);
	}, [data]);

	/**
	 * Refresh the Bouncy Chest state when the Pacific calendar day changes.
	 * DIPs are refreshed separately by useInitialActions.
	 */
	useEffect(() => {
		if (newDay) {
			refetch();
		}
	}, [newDay]);

	/**
	 * Resume a chest click that was deferred while the user was logging in.
	 */
	useEffect(() => {
		if (
			localStorage.getItem(LOCAL_STORAGE_LOGIN_FROM) ===
				"login_to_continue_dialog#bouncyChest" &&
			!isDisabled() &&
			authToken?.value
		) {
			localStorage.setItem(LOCAL_STORAGE_LOGIN_FROM, "");
			dispatch(onDIPtrigger({ triggerType: "bouncyChestClick" }));
		}
	}, [authToken?.value, isDisabled]);

	/**
	 * Clear the deferred-login marker if the chest was already claimed.
	 */
	useEffect(() => {
		if (
			localStorage.getItem(LOCAL_STORAGE_LOGIN_FROM) ===
				"login_to_continue_dialog#bouncyChest" &&
			isClaimed()
		) {
			localStorage.setItem(LOCAL_STORAGE_LOGIN_FROM, "");
		}
	}, [authToken?.value, isClaimed]);

	if (from === undefined && (isMegaMenuOpen || isHomePage)) {
		return null;
	}

	if (isDisabled()) {
		return null;
	}

	return (
		<Container
			className={`animate animate__animated animate__fadeIn ${from}`}
			onClick={onClick}
			filter={isDisabled() ? 1 : 0}
		>
			<ChestImage
				className={`${from}`}
				src={bouncyChestConfig?.chestIconImageUrl || chestImage}
			/>
			<StarBurst className={`${from}`} src={starBurst}></StarBurst>
		</Container>
	);
};
